import { Reference, StoreObject } from '@apollo/client';
import { FieldFunctionOptions } from '@apollo/client/cache/inmemory/policies';

export function merge<T extends Reference | StoreObject | undefined>(
  existing: T[],
  incoming: T[],
  { readField, mergeObjects, canRead }: FieldFunctionOptions,
) {
  // Filtering by canRead to remove any dangling references left
  // over after evicting from the cache.
  // https://www.apollographql.com/docs/react/caching/garbage-collection/#dangling-references
  const merged: any[] = existing
    ? [...existing.filter(node => canRead(node))]
    : [];

  // Record of nodes we have in existing
  const idToIndex: Record<string, number> = Object.create(null);

  // Try to read the ID field, assuming objet is a Node type.
  // If no ID assume object is an Edge type and try and get the
  // ID from the edge node.
  function getId(object: Reference | StoreObject | undefined) {
    let id = readField<string>('id', object);

    if (id) {
      return id;
    }

    const node = readField<Node>('node', object);

    if (!node) {
      throw new Error(
        `No ID and no further node to find ID on ${JSON.stringify(object)}`,
      );
    }

    const nodeId = readField<string>('id', node);

    if (!nodeId) {
      throw new Error('ID field needed in edge node');
    }

    return nodeId;
  }

  // We need to keep a record of nodes we currently have
  if (merged) {
    merged.forEach((object, index) => {
      const id = getId(object);

      idToIndex[id] = index;
    });
  }

  // Check if we have the incoming node already in our list.
  incoming.forEach(object => {
    const id = getId(object);

    const index = idToIndex[id];

    if (typeof index === 'number') {
      // If we have the node, merge the objects together
      const mergedObject = mergeObjects(merged[index], object);

      if (!mergedObject) {
        throw new Error('Object merge failed');
      }

      merged[index] = mergedObject;
    } else {
      // First time we've seen this node, add it to our array
      idToIndex[id] = merged.length;

      merged.push(object);
    }
  });

  return merged;
}
